import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";

function ServiceItemsPage() {
  const [serviceItems, setServiceItems] = useState([]);
  const [serviceId, setServiceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("service_id");

  useEffect(() => {
    if (id) {
      setServiceId(id);
      fetchServiceItems(id);
    }
  }, [searchParams]);

  const fetchServiceItems = (id) => {
    setLoading(true);
    API.getServiceItems(API.cancelToken(), id)
      .then((res) => {
        setServiceItems(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const translateStatus = (status) => {
    switch (status) {
      case "NOT_STARTED":
        return "Não Iniciado";
      case "IN_PROGRESS":
        return "Iniciado";
      case "FINISHED":
        return "Finalizado";
      default:
        return status;
    }
  };

  const handleStart = (itemId) => {
    Swal.fire({
      title: "Iniciar serviço",
      text: "Tem certeza que deseja iniciar este item?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, iniciar",
    }).then((result) => {
      if (result.isConfirmed) {
        API.startServiceItem(API.cancelToken(), itemId).then(() => {
          fetchServiceItems(serviceId);
        });
      }
    });
  };

  const handleFinish = (itemId) => {
    Swal.fire({
      title: "Finalizar serviço",
      text: "Tem certeza que deseja finalizar este item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, finalizar",
    }).then((result) => {
      if (result.isConfirmed) {
        API.finishServiceItem(API.cancelToken(), itemId).then(() => {
          fetchServiceItems(serviceId);
        });
      }
    });
  };

  const renderActionButton = (item) => {
    if (item.status === "NOT_STARTED") {
      return (
        <button
          onClick={() => handleStart(item.service_item_id)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          Iniciar
        </button>
      );
    }
    if (item.status === "IN_PROGRESS") {
      return (
        <button
          onClick={() => handleFinish(item.service_item_id)}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
        >
          Finalizar
        </button>
      );
    }
    return <p className="text-green-700 font-semibold text-sm">Finalizado ✅</p>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).add(3, 'hours').format("DD/MM/YYYY HH:mm");
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "FINISHED":
        return "bg-green-600 text-white";
      case "NOT_STARTED":
        return "bg-yellow-400 text-black";
      case "IN_PROGRESS":
        return "bg-blue-500 text-white";
      default:
        return "bg-gray-300 text-black";
    }
  };

  return (
    <div className="flex flex-col w-full p-4 md:p-8 bg-gray-100">
      <h2 className="text-xl md:text-2xl font-bold mb-6">
        Itens da Ordem de Serviço #{serviceId}
      </h2>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div className="bg-white shadow rounded p-4 overflow-x-auto">
          <table className="min-w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 font-semibold">
              <tr>
                <th className="py-1 px-2 md:py-2 md:px-4">OS</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Título</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Observação</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Status</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Início</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Término</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Tempo de Execução</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Responsável</th>
                <th className="py-1 px-2 md:py-2 md:px-4">Ação</th>
              </tr>
            </thead>
            <tbody>
              {serviceItems.length > 0 ? (
                serviceItems.map((item) => (
                  <tr key={item.service_item_id} className="border-b">
                    <td className="py-1 px-2 md:py-2 md:px-4">{serviceId}</td>
                    <td className="py-1 px-2 md:py-2 md:px-4">{item.service_item_title}</td>
                    <td className="py-1 px-2 md:py-2 md:px-4">{item.service_item_annotation || "-"}</td>
                    <td className="py-1 px-2 md:py-2 md:px-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadgeColor(item.status)}`}>
                        {translateStatus(item.status)}
                      </span>
                    </td>
                    <td className="py-1 px-2 md:py-2 md:px-4">{formatDate(item.started_at)}</td>
                    <td className="py-1 px-2 md:py-2 md:px-4">{formatDate(item.finished_at)}</td>
                    <td className="py-1 px-2 md:py-2 md:px-4">
                      {item.started_at && item.finished_at
                        ? `${Math.round((new Date(item.finished_at) - new Date(item.started_at)) / 60000)} min`
                        : "-"}
                    </td>
                    <td className="py-1 px-2 md:py-2 md:px-4">{item.started_by_user_name || "-"}</td>
                    <td className="py-1 px-2 md:py-2 md:px-4">{renderActionButton(item)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="py-4 text-center">
                    Nenhum item de serviço encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ServiceItemsPage;
